// LpManageModal.jsx
import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import ContractContext from '../../ContractContext';
import Web3 from 'web3';
import ierc20Abi from '../../contracts/ierc20Abi';
import routerAbi from '../../contracts/routerAbi';
import pairAbi from '../../contracts/pairAbi';
import { formatDecimals, formatCurrencyExtended } from '../../utils';
import BigNumber from 'bignumber.js';
import { toast } from 'react-toastify'; // Import toast

const LpManageModal = ({
  tokenSelections,
  visible,
  onClose,
  lpData,
  account,
  provider,
  bnbPrice,
  privacyMode,
  refreshData
}) => {
  const contractContext = useContext(ContractContext);
  const contracts = contractContext.contracts;
  // Tab states: 'add' | 'remove'
  const [tab, setTab] = useState('add');

  // Input amounts for "Add" side
  const [amountToken, setAmountToken] = useState('');
  const [amountBacking, setAmountBacking] = useState('');
  const [lastEditedSide, setLastEditedSide] = useState(null);

  // "Remove" side
  const [removePercent, setRemovePercent] = useState(0);
  const [removeAmount, setRemoveAmount] = useState('')

  // User balances for token/backing
  const [userTokenBalance, setUserTokenBalance] = useState('0');
  const [userBackingBalance, setUserBackingBalance] = useState('0');

  // Allowances for token/backing
  const [tokenAllowance, setTokenAllowance] = useState('0');
  const [backingAllowance, setBackingAllowance] = useState('0');

  // LP token allowance
  const [lpTokenAllowance, setLpTokenAllowance] = useState('0');

  // Slippage
  const [slippage, setSlippage] = useState('1.5');
  const slippageFraction = parseFloat(slippage) / 100 || 0.005;
  const oneMinusSlippage = 1 - slippageFraction;

  // Track loading
  const [isLoading, setIsLoading] = useState(false);

  // Ratio for automatically populating one side
  const [tokenToBackingRatio, setTokenToBackingRatio] = useState(null);

  // From lpData
  const tokenSymbol = lpData?.tokenSymbol || 'TokenA';
  const backingSymbol = lpData?.backingTokenSymbol || 'TokenB';
  const routerAddress = lpData?.router || '';
  const lpTokenDecimals = lpData?.decimals;

  // BNB check
  const WBNB = '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c';
  const isTokenBNB = lpData?.token?.toLowerCase() === WBNB;
  const isBackingBNB = lpData?.backingToken?.toLowerCase() === WBNB;
  const isOneSideBNB = isTokenBNB || isBackingBNB;

  // Reuse or create Web3
  const web3 = useMemo(() => {
    if (provider) {
      return new Web3(provider);
    }
    return null;
  }, [provider]);

  const resetInputs = () => {
    setAmountToken('');
    setAmountBacking('');
    setRemoveAmount('');
    setRemovePercent(0);
    setLastEditedSide(null);
  };

  const handleClose = () => {
    resetInputs();
    onClose();
  };


  // 1) Fetch pool ratio (for auto-populating the other side)
  useEffect(() => {
    if (!lpData?.pairAddress || !web3) return;
    const pairContract = new web3.eth.Contract(pairAbi, lpData.pairAddress);

    (async () => {
      try {
        const token0 = await pairContract.methods.token0().call();
        const token1 = await pairContract.methods.token1().call();
        const { _reserve0, _reserve1 } = await pairContract.methods.getReserves().call();

        // const dec0 = lpData.tokenDecimals;
        // const dec1 = lpData.backingTokenDecimals;
        const dec0 = token0 === lpData.token ? lpData.tokenDecimals : lpData.backingTokenDecimals;
        const dec1 = token1 === lpData.token ? lpData.tokenDecimals : lpData.backingTokenDecimals;

        const reserve0BN = new BigNumber(_reserve0).div(new BigNumber(10).pow(dec0));
        const reserve1BN = new BigNumber(_reserve1).div(new BigNumber(10).pow(dec1));

        // Check if lpData.token is token0 or token1
        if (token0.toLowerCase() === lpData.token?.toLowerCase()) {
          // ratio = reserve1 / reserve0
          const ratio = reserve1BN.div(reserve0BN);
          setTokenToBackingRatio(ratio);
        } else {
          // ratio = reserve0 / reserve1
          const ratio = reserve0BN.div(reserve1BN);
          setTokenToBackingRatio(ratio);
        }
      } catch (err) {
        console.error('Failed reading pair reserves:', err);
        setTokenToBackingRatio(null);
      }
    })();
  }, [
    lpData?.pairAddress,
    lpData?.token,
    lpData?.tokenDecimals,
    lpData?.backingTokenDecimals,
    web3
  ]);

  useEffect(() => {
    if (!lpData || !account || !routerAddress || !visible || !web3) return;
    let isMounted = true;

    const fetchData = async () => {
      try {
        // A) Token/backing balances
        let tokenBal = new BigNumber(0);
        let backingBal = new BigNumber(0);

        if (!isTokenBNB) {
          const c = new web3.eth.Contract(ierc20Abi, lpData.token);
          const raw = await c.methods.balanceOf(account).call();
          tokenBal = new BigNumber(raw);
        } else {
          const raw = await web3.eth.getBalance(account);
          tokenBal = new BigNumber(raw);
        }

        if (!isBackingBNB) {
          const c = new web3.eth.Contract(ierc20Abi, lpData.backingToken);
          const raw = await c.methods.balanceOf(account).call();
          backingBal = new BigNumber(raw);
        } else {
          const raw = await web3.eth.getBalance(account);
          backingBal = new BigNumber(raw);
        }

        if (isMounted) {
          setUserTokenBalance(tokenBal.toFixed());
          setUserBackingBalance(backingBal.toFixed());
        }
      } catch (err) {
        console.error('Error fetching balances:', err);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [
    account,
    routerAddress,
    lpData?.token,
    lpData?.backingToken,
    isTokenBNB,
    isBackingBNB,
    visible,
    web3
  ]);

  // 3. Separate the allowance fetching into its own useEffect
  useEffect(() => {
    if (!lpData || !account || contracts.lpSeeder._address
      //!routerAddress 
      || !visible || !web3) return;
    let isMounted = true;

    const fetchAllowances = async () => {
      try {
        let tokenAllowBN = new BigNumber('0');
        let backingAllowBN = new BigNumber('0');
        let lpAllowBN = new BigNumber('0');

        if (!isTokenBNB) {
          const c = new web3.eth.Contract(ierc20Abi, lpData.token);
          //const raw = await c.methods.allowance(account, routerAddress).call();
          const raw = await c.methods.allowance(account, contracts.lpSeeder._address).call();
          tokenAllowBN = new BigNumber(raw);
        }

        if (!isBackingBNB) {
          const c = new web3.eth.Contract(ierc20Abi, lpData.backingToken);
          //const raw = await c.methods.allowance(account, routerAddress).call();
          const raw = await c.methods.allowance(account, contracts.lpSeeder._address).call();
          backingAllowBN = new BigNumber(raw);
        }

        if (lpData.pairAddress) {
          const lpC = new web3.eth.Contract(ierc20Abi, lpData.pairAddress);
          //const raw = await lpC.methods.allowance(account, routerAddress).call();
          const raw = await lpC.methods.allowance(account, contracts.lpSeeder._address).call();
          lpAllowBN = new BigNumber(raw);
        }

        if (isMounted) {
          setTokenAllowance(tokenAllowBN.toFixed());
          setBackingAllowance(backingAllowBN.toFixed());
          setLpTokenAllowance(lpAllowBN.toFixed());
        }
      } catch (err) {
        console.error('Error fetching allowances:', err);
      }
    };

    fetchAllowances();
    return () => { isMounted = false; };
  }, [
    account,
    routerAddress,
    lpData?.token,
    lpData?.backingToken,
    lpData?.pairAddress,
    isTokenBNB,
    isBackingBNB,
    visible,
    web3
  ]);

  // 3) Auto-calc other side
  useEffect(() => {
    if (!tokenToBackingRatio) return;
    if (!lastEditedSide) return;

    let valBN = new BigNumber(0);
    if (lastEditedSide === 'token' && amountToken) {
      valBN = new BigNumber(amountToken);
    } else if (lastEditedSide === 'backing' && amountBacking) {
      valBN = new BigNumber(amountBacking);
    }
    if (valBN.lte(0)) return;

    if (lastEditedSide === 'token') {
      // backing = ratio * token
      const newBacking = tokenToBackingRatio.times(valBN);
      setAmountBacking(newBacking.toFixed(6));
    } else {
      // token = backing / ratio
      const newToken = valBN.div(tokenToBackingRatio);
      setAmountToken(newToken.toFixed(6));
    }
  }, [tokenToBackingRatio, lastEditedSide, amountToken, amountBacking]);

  // Helpers
  const getTokenInfo = useCallback((side) => {
    if (side === 'token') {
      const decimals = lpData?.tokenDecimals;
      const rawBnbPrice = lpData.tokenPriceInBNB || '0';
      const priceInUsd = new BigNumber(rawBnbPrice)
        .div(new BigNumber(10).pow(18)) // Assuming BNB price is in 18 decimals
        .times(bnbPrice)
        .toNumber();
      //console.log('Token Info (Token):', { decimals, rawBnbPrice, priceInUsd });
      return { decimals, price: priceInUsd, isBNB: isTokenBNB };
    } else {
      const decimals = lpData?.backingTokenDecimals;
      const rawBnbPrice = lpData.backingTokenPriceInBNB || '0';
      const priceInUsd = new BigNumber(rawBnbPrice)
        .div(new BigNumber(10).pow(18))
        .times(bnbPrice)
        .toNumber();
      //console.log('Token Info (Backing):', { decimals, rawBnbPrice, priceInUsd });
      return { decimals, price: priceInUsd, isBNB: isBackingBNB };
    }
  }, [lpData, bnbPrice, isTokenBNB, isBackingBNB]);

  // const getTokenInfo = useCallback((side) => {
  //   // side: 'token' | 'backing'
  //   if (side === 'token') {
  //     //tokenSelections.find((t) => t.value.toLowerCase() === lpData.token.toLowerCase()).decimals
  //     const decimals = lpData?.tokenDecimals;
  //     console.log("Get Token Info: ", decimals);  // Debugging
  //     const rawBnbPrice = lpData.tokenPriceInBNB || '0';
  //     const priceInUsd = new BigNumber(rawBnbPrice).div( new BigNumber(10).pow(18)).times(bnbPrice).toNumber();
  //     return { decimals, price: priceInUsd, isBNB: isTokenBNB };
  //   } else {
  //     const decimals = lpData?.backingTokenDecimals;
  //     console.log("Get Backing Info: ", decimals);  // Debugging
  //     const rawBnbPrice = lpData.backingTokenPriceInBNB || '0';
  //     const priceInUsd = new BigNumber(rawBnbPrice).div( new BigNumber(10).pow(18)).times(bnbPrice).toNumber();
  //     return { decimals, price: priceInUsd, isBNB: isBackingBNB };
  //   }
  // }, [lpData, bnbPrice, isTokenBNB, isBackingBNB, tokenSelections]);

  const tokenUsdValue = (() => {
    const { price } = getTokenInfo('token');
    if (!amountToken) return 0;
    const calculatedValue = new BigNumber(amountToken)
      .times(price)
      .toFixed(6); // Force precision
    // console.log('Token USD Value:', {
    //   amountToken,
    //   price,
    //   calculatedValue,
    // });
    return parseFloat(calculatedValue);
  })();

  const backingUsdValue = (() => {
    const { price } = getTokenInfo('backing');
    if (!amountBacking) return 0;
    return parseFloat(
      new BigNumber(amountBacking)
        .times(price)
        .toFixed(6)
    );
  })();

  const totalAddUsd = parseFloat(
    (parseFloat(tokenUsdValue) + parseFloat(backingUsdValue)).toFixed(6)
  );


  // For remove side
  const totalLpValueUsd = lpData.approxUsdValue || lpData.yourLpUSDValue || 0;
  const removeUsdValue = (() => {
    if (lpData?.lpTokenBalance && removeAmount) {
      const lpBalance = new BigNumber(lpData.lpTokenBalance).div(new BigNumber(10).pow(lpTokenDecimals));
      const removeAmt = new BigNumber(removeAmount);
      return lpData.approxUsdValue * (removeAmt.div(lpBalance).toNumber());
    }
    return 0;
  })();


  const handleQuickFillRemove = (pct) => {
    const lpBalanceBN = new BigNumber(lpData.lpTokenBalance).div(new BigNumber(10).pow(lpTokenDecimals));
    const fillVal = pct !== 1
      ? lpBalanceBN.times(pct)//.toFixed(6)
      : lpBalanceBN.times(0.9999)//.toFixed(6); // Avoid rounding errors for 100%
    setRemoveAmount(fillVal);
    setRemovePercent(pct * 100);
  };


  // Quick fill
  // const handleQuickFill = (side, pct) => {
  //   // side: 'token' | 'backing'
  //   const { decimals } = getTokenInfo(side);
  //   const rawBal = side === 'token' ? userTokenBalance : userBackingBalance;
  //   const balBN = new BigNumber(rawBal);
  //   const humanBal = balBN.div(new BigNumber(10).pow(decimals));
  //   //if taking max, set 99.9999999% to avoid rounding errors
  //   const fillVal = pct !== 1 ?
  //     humanBal.times(pct).toFixed(6)
  //     : humanBal.times(0.9999).toFixed(6);

  //   setLastEditedSide(side);
  //   if (side === 'token') setAmountToken(fillVal);
  //   else setAmountBacking(fillVal);
  // };

  const handleQuickFill = (side, pct) => {
    const { decimals } = getTokenInfo(side);
    const rawBal = side === 'token' ? userTokenBalance : userBackingBalance;
    const balBN = new BigNumber(rawBal);
    const humanBal = balBN.div(new BigNumber(10).pow(decimals));
    // console.log('Quick Fill Debug:', {
    //   side,
    //   pct,
    //   rawBal,
    //   humanBal: humanBal.toString(),
    // });
    const fillVal =
      pct !== 1
        ? humanBal.times(pct)//.toFixed(6)
        : humanBal.times(0.9999)//.toFixed(6);
    setLastEditedSide(side);
    if (side === 'token') setAmountToken(fillVal);
    else setAmountBacking(fillVal);
  };


  const onChangeRemoveAmount = (val) => {
    const lpBalanceBN = new BigNumber(lpData.lpTokenBalance).div(new BigNumber(10).pow(lpTokenDecimals));
    const inputAmountBN = new BigNumber(val);
    const pct = lpBalanceBN.eq(0) ? 0 : inputAmountBN.div(lpBalanceBN).times(100);

    // Ensure the percentage does not exceed 100%
    const finalPct = pct.gt(100) ? 100 : pct;

    setRemoveAmount(val);
    setRemovePercent(finalPct.toNumber());
  };


  const onChangeAmountToken = (val) => {
    setLastEditedSide('token');
    setAmountToken(val);
  };

  const onChangeAmountBacking = (val) => {
    setLastEditedSide('backing');
    setAmountBacking(val);
  };

  const handleSlippageChange = (val) => {
    setSlippage(val);
  };

  // Approve logic (token/backing) for Add
  const handleApproveToken = useCallback(async () => {
    if (!lpData?.token) return;
    try {
      setIsLoading(true);
      const c = new web3.eth.Contract(ierc20Abi, lpData.token);
      const { decimals } = getTokenInfo('token');
      const desiredWei = new BigNumber(amountToken).times(new BigNumber(10).pow(decimals)).toFixed(0);

      const tx = await c.methods.approve(contracts.lpSeeder._address, desiredWei).send({ from: account });
      //const tx = await c.methods.approve(routerAddress, desiredWei).send({ from: account });
      toast.success('Token Approved!');
      // Re-fetch
      const newAllow = await c.methods.allowance(account, contracts.lpSeeder._address).call();
      //const newAllow = await c.methods.allowance(account, routerAddress).call();
      setTokenAllowance(newAllow);
    } catch (err) {
      console.error('Approve token error:', err);
      toast.error('Approve Token Failed.');
    } finally {
      setIsLoading(false);
    }
  }, [account, lpData?.token, web3, amountToken, getTokenInfo]);

  const handleApproveBacking = useCallback(async () => {
    if (!lpData?.backingToken) return;
    try {
      setIsLoading(true);
      const c = new web3.eth.Contract(ierc20Abi, lpData.backingToken);

      const { decimals } = getTokenInfo('backing');
      const desiredWei = new BigNumber(amountBacking).times(new BigNumber(10).pow(decimals)).toFixed(0);

      const tx = await c.methods.approve(contracts.lpSeeder._address, desiredWei).send({ from: account });
      //const tx = await c.methods.approve(routerAddress, desiredWei).send({ from: account });
      toast.success('Backing Token Approved!');
      // Re-fetch
      const newAllow = await c.methods.allowance(account, contracts.lpSeeder._address).call();
      //const newAllow = await c.methods.allowance(account, routerAddress).call();
      setBackingAllowance(newAllow);
    } catch (err) {
      console.error('Approve backing error:', err);
      toast.error('Approve Backing Failed.');
    } finally {
      setIsLoading(false);
    }
  }, [account, lpData?.backingToken, web3, amountBacking, getTokenInfo]);

  // For "Add" side, do we need approvals?
  const needsTokenApproval = useCallback(() => {
    if (isTokenBNB) return false;
    if (!amountToken) return false;
    const { decimals } = getTokenInfo('token');
    const desiredWei = new BigNumber(amountToken).times(new BigNumber(10).pow(decimals));
    return new BigNumber(tokenAllowance).lt(desiredWei);
  }, [amountToken, tokenAllowance, getTokenInfo, isTokenBNB]);

  const needsBackingApproval = useCallback(() => {
    if (isBackingBNB) return false;
    if (!amountBacking) return false;
    const { decimals } = getTokenInfo('backing');
    const desiredWei = new BigNumber(amountBacking).times(new BigNumber(10).pow(decimals));
    return new BigNumber(backingAllowance).lt(desiredWei);
  }, [amountBacking, backingAllowance, getTokenInfo, isBackingBNB]);

  const canAddLiquidity = !needsTokenApproval() && !needsBackingApproval() && !isLoading;

  // Actual Add Liquidity
  const handleAddLiquidity = useCallback(async () => {
    if (!lpData || !routerAddress) return;
    try {
      setIsLoading(true);

      const routerContract = new web3.eth.Contract(routerAbi, routerAddress);
      const amtTokenBN = new BigNumber(amountToken);
      const amtBackingBN = new BigNumber(amountBacking);

      if (amtTokenBN.lte(0) || amtBackingBN.lte(0)) {
        toast.error('Please enter valid amounts.');
        return;
      }

      const { decimals: tokenDec } = getTokenInfo('token');
      const { decimals: backingDec } = getTokenInfo('backing');
      const tokenWeiDesired = amtTokenBN.times(new BigNumber(10).pow(tokenDec)).toFixed(0);
      const backingWeiDesired = amtBackingBN.times(new BigNumber(10).pow(backingDec)).toFixed(0);


      const tokenWeiMin = new BigNumber(tokenWeiDesired).times(oneMinusSlippage).toFixed(0);
      const backingWeiMin = new BigNumber(backingWeiDesired).times(oneMinusSlippage).toFixed(0);

      const deadline = Math.floor(Date.now() / 1000) + (60 * 20);

      // NEW CODE = Code below with 6 parameters is using the addLiquidityETH function directly from the dex contract
      // The new code is using the addLiquidity function from the LP Seeder contract
      //   await contracts.lpSeeder.methods.addLiquidityETH(
      //     lpData.backingToken,
      //     backingWeiDesired,
      //     backingWeiMin,
      //     routerAddress,
      //     deadline
      //   ).send({
      //     from: account,
      //     value: tokenWeiDesired
      //   });
      // } else {
      //   // Backing side BNB
      //   await contracts.lpSeeder.methods.addLiquidityETH(
      //     lpData.token,
      //     tokenWeiDesired,
      //     tokenWeiMin,
      //     routerAddress,
      //     deadline
      //   ).send({
      //     from: account,
      //     value: backingWeiDesired
      //   });

      if (isOneSideBNB) {
        if (isTokenBNB) {
          // Token side BNB
          console.log('Add Liquidity ETH:', { backingWeiDesired, backingWeiMin, tokenWeiMin, routerAddress, deadline });
          await contracts.lpSeeder.methods.addLiquidityETH(
          //await routerContract.methods.addLiquidityETH(
            lpData.backingToken,
            backingWeiDesired,
            backingWeiMin,
            tokenWeiMin,
            routerAddress,
            //account,
            deadline
          ).send({
            from: account,
            value: tokenWeiDesired
          });
        } else {
          console.log('Add Liquidity ETH:', { tokenWeiDesired, tokenWeiMin, backingWeiMin, routerAddress, deadline });
          // Backing side BNB
          await contracts.lpSeeder.methods.addLiquidityETH(
          //await routerContract.methods.addLiquidityETH(
            lpData.token,
            tokenWeiDesired,
            tokenWeiMin,
            backingWeiMin,
            routerAddress,
            //account,
            deadline
          ).send({
            from: account,
            value: backingWeiDesired
          });
        }
      } else {
        // Normal addLiquidity
        console.log('Add Liquidity:', { tokenWeiDesired, backingWeiDesired, tokenWeiMin, backingWeiMin, routerAddress, deadline });
        await contracts.lpSeeder.methods.addLiquidity(
        //await routerContract.methods.addLiquidity(
          lpData.token,
          lpData.backingToken,
          tokenWeiDesired,
          backingWeiDesired,
          tokenWeiMin,
          backingWeiMin,
          routerAddress,
          //account,
          deadline
        ).send({ from: account });
      }

      toast.success('Add Liquidity Succeeded!');
      await refreshData(); // Refresh data after successful transaction
      handleClose();
    } catch (error) {
      console.error('Add Liquidity error:', error);
      // Handle specific error messages if available
      const errorMessage = error?.message || 'Add Liquidity failed.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [
    lpData,
    routerAddress,
    account,
    amountToken,
    amountBacking,
    oneMinusSlippage,
    isTokenBNB,
    isOneSideBNB,
    getTokenInfo,
    web3,
    refreshData,
    contracts.lpSeeder
  ]);

  // LP Approve logic for "Remove Liquidity"
  const handleApproveLpToken = useCallback(async () => {
    // We want to do an infinite approve for the LP token if needed
    if (!lpData?.pairAddress) return;
    try {
      setIsLoading(true);
      const lpC = new web3.eth.Contract(ierc20Abi, lpData.pairAddress);
      const removeWei = new BigNumber(removeAmount).times(new BigNumber(10).pow(lpTokenDecimals)).toFixed(0);

      const tx = await lpC.methods.approve(contracts.lpSeeder._address, removeWei).send({ from: account });
      //const tx = await lpC.methods.approve(routerAddress, removeWei).send({ from: account });
      toast.success('LP Token Approved!');
      // Re-fetch LP allowance
      const newAllow = await lpC.methods.allowance(account, contracts.lpSeeder._address).call();
      //const newAllow = await lpC.methods.allowance(account, routerAddress).call();
      setLpTokenAllowance(newAllow);
    } catch (err) {
      console.error('Approve LP token error:', err);
      toast.error('Approve LP Token Failed.');
    } finally {
      setIsLoading(false);
    }
  }, [lpData?.pairAddress, account, web3, removeAmount, lpTokenDecimals]);

  // Check if we need to approve LP for removing
  const needsLpApproval = useCallback(() => {
    if (!lpData?.lpTokenBalance || !removeAmount) return false;
    const lpBalanceBN = new BigNumber(lpData.lpTokenBalance).div(new BigNumber(10).pow(lpTokenDecimals));
    const removeAmtBN = new BigNumber(removeAmount);
    return new BigNumber(lpTokenAllowance).lt(removeAmtBN.times(new BigNumber(10).pow(lpTokenDecimals)));
  }, [lpData?.lpTokenBalance, lpTokenAllowance, removeAmount, lpTokenDecimals]);


  // Enable "Remove Liquidity" only if user typed a nonzero removePercent & not loading & LP is approved
  const canRemoveLiquidity = !isLoading && removePercent > 0 && !needsLpApproval();

  // Actual Remove Liquidity
  const handleRemoveLiquidity = useCallback(async () => {
    if (!lpData || !routerAddress) return;
    try {
      setIsLoading(true);

      const routerContract = new web3.eth.Contract(routerAbi, routerAddress);
      const lpBalanceBN = new BigNumber(lpData.lpTokenBalance || 0).div(new BigNumber(10).pow(lpTokenDecimals));

      const removeAmountBN = new BigNumber(removeAmount);
      if (removeAmountBN.lte(0)) {
        toast.error('Please enter a valid amount to remove.');
        return;
      }
      if (removeAmountBN.gt(lpBalanceBN)) {
        toast.error('Remove amount exceeds your LP token balance.');
        return;
      }

      const removeWeiBN = removeAmountBN.times(new BigNumber(10).pow(lpTokenDecimals));
      const removeWei = removeWeiBN.toFixed(0);

      const minAmt = '0';
      const deadline = Math.floor(Date.now() / 1000) + (60 * 20);

      if (isOneSideBNB) {
        const normalToken = isTokenBNB ? lpData.backingToken : lpData.token;
        console.log('Remove Liquidity ETH:', { normalToken, removeWei, minAmt, account, deadline });
        await contracts.lpSeeder.methods.removeLiquidityETHSupportingFeeOnTransferTokens(
        //await routerContract.methods.removeLiquidityETHSupportingFeeOnTransferTokens(
          normalToken,
          removeWei,
          minAmt,
          minAmt,
          routerAddress,
          //account,
          deadline
        ).send({ from: account });
      } else {
        await contracts.lpSeeder.methods.removeLiquidity(
        //await routerContract.methods.removeLiquidity(
          lpData.token,
          lpData.backingToken,
          removeWei,
          minAmt,
          minAmt,
          routerAddress,
          //account,
          deadline
        ).send({ from: account });
      }

      toast.success('Remove Liquidity Succeeded!');
      await refreshData(); // Refresh data after successful transaction
      handleClose();
    } catch (error) {
      console.error('Remove liquidity error:', error);
      const errorMessage = error?.message || 'Remove Liquidity failed.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [
    lpData,
    routerAddress,
    account,
    removeAmount,
    isOneSideBNB,
    isTokenBNB,
    web3,
    refreshData,
    lpTokenDecimals
  ]);

  // Rendering
  const footer = (
    <div style={{ textAlign: 'right' }}>
      <Button label="Close" onClick={onClose} className="p-button-text" />
    </div>
  );

  if (!lpData) return null;

  const yourLpUSDValue = lpData.approxUsdValue || lpData.yourLpUSDValue || 0;

  console.log("userTokenBalance, lpData.tokenDecimals, tokenSymbol ", userTokenBalance, lpData.tokenDecimals, tokenSymbol);  // Debugging
  console.log("userBackingBalance, lpData.backingTokenDecimals, backingSymbol ", userBackingBalance, lpData.backingTokenDecimals, backingSymbol);  // Debugging

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '600px', borderRadius: '10px' }}
      header="Manage Liquidity"
      footer={footer}
      draggable={false}
      resizable={false}
    >
      {/* Tab Switch */}
      <div style={{
        display: 'flex', gap: '5px', marginBottom: '10px',
        padding: '10px', borderRadius: '10px', backgroundColor: 'black',


      }}>
        <Button
          label="Add Liquidity"
          className={tab === 'add' ? 'p-button-info' : 'p-button-outlined p-button-info'}
          onClick={() => setTab('add')}
        />
        <Button
          label="Remove Liquidity"
          className={tab === 'remove' ? 'p-button-danger' : 'p-button-outlined p-button-danger'}
          onClick={() => setTab('remove')}
        />
      </div>


      {/* Slippage */}
      <div style={{ marginBottom: '10px' }}>
        <label>Slippage Tolerance (%)</label>
        <InputText
          placeholder="0.5"
          value={slippage}
          onChange={(e) => handleSlippageChange(e.target.value)}
          style={{ width: '100px', marginLeft: '10px' }}
          disabled={isLoading}
        />
      </div>

      {tab === 'add' && (
        <>
          <h3 style={{ marginTop: '0' }}>Add Liquidity</h3>

          {/* TOKEN SIDE */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <label style={{ fontWeight: 'bold' }}>{tokenSymbol} Balance:</label>
              {!privacyMode ? (
                <span>
                  {formatDecimals(userTokenBalance, lpData.tokenDecimals)} {tokenSymbol}
                </span>
              ) : (
                <span>*****</span>
              )}
            </div>
            {/* Quick Fill */}
            <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
              {[25, 50, 100].map((pct) => (
                <Button
                  key={pct}
                  label={`${pct}%`}
                  onClick={() => handleQuickFill('token', pct / 100)}
                  className="p-button-sm p-button-outlined"
                  disabled={isLoading}
                />
              ))}
            </div>
            <InputText
              placeholder="0.0"
              value={amountToken}
              onChange={(e) => onChangeAmountToken(e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
              disabled={isLoading}
            />


            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '5px',
              marginBottom: '5px'
            }}>
              {!privacyMode && (
                <small style={{ color: 'gray' }}>
                  ~{formatCurrencyExtended(tokenUsdValue)}
                </small>
              )}
            </div>
          </div>

          {/* BACKING SIDE */}
          <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <label style={{ fontWeight: 'bold' }}>{backingSymbol} Balance:</label>
              {!privacyMode ? (
                <span>
                  {formatDecimals(userBackingBalance, lpData.backingTokenDecimals)} {backingSymbol}
                </span>
              ) : (
                <span>*****</span>
              )}
            </div>
            {/* Quick Fill */}
            <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
              {[25, 50, 100].map((pct) => (
                <Button
                  key={pct}
                  label={`${pct}%`}
                  onClick={() => handleQuickFill('backing', pct / 100)}
                  className="p-button-sm p-button-outlined"
                  disabled={isLoading}
                />
              ))}
            </div>
            <InputText
              placeholder="0.0"
              value={amountBacking}
              onChange={(e) => onChangeAmountBacking(e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
              disabled={isLoading}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '5px',
              marginBottom: '5px'
            }}>
              {!privacyMode && (
                <small style={{ color: 'gray' }}>
                  ~{formatCurrencyExtended(backingUsdValue)}
                </small>
              )}

       
            </div>
          </div>

          {!privacyMode && (
            <div style={{ marginBottom: '15px', textAlign: 'right', fontWeight: 'bold' }}>
              Total: {formatCurrencyExtended(totalAddUsd)}
            </div>
          )}

          <div style={{ 
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
             {!isTokenBNB && parseFloat(amountToken) > 0 && needsTokenApproval() && (
                <Button
                  label={`Approve` + ` ${formatDecimals(amountToken,0)}` + ` ${tokenSymbol}`}
                  onClick={handleApproveToken}
                  disabled={isLoading}
                  className="p-button-warning p-button-sm"
                  style={{ 
                    width: '100%',
             marginTop: '10px',
             marginLeft: '10px',
             marginRight: '10px'
                   }}
                />
              )}
              {!isBackingBNB && parseFloat(amountBacking) > 0 && needsBackingApproval() && (
                <Button
                  label={`Approve` + ` ${formatDecimals(amountBacking,0)}` + ` ${backingSymbol}`}
                  onClick={handleApproveBacking}
                  disabled={isLoading}
                  className="p-button-warning p-button-sm"
                  style={{ 
                    width: '100%',
               marginTop: '10px',
                marginLeft: '10px',
                marginRight: '10px'
                   }}
                />
              )}

                    { !needsTokenApproval() && !needsBackingApproval() && 
            <Button
            label={isLoading ? 'Processing...' : 'Add Liquidity'}
            onClick={handleAddLiquidity}
            disabled={!canAddLiquidity}
            className="p-button-success"
            style={{ width: '100%', marginTop: '10px',
              marginLeft: '10px',
              marginRight: '10px'
            }}
          />}
          </div>

    
        </>
      )}

      {tab === 'remove' && (
        <>
          <h3 style={{ marginTop: '0' }}>Remove Liquidity</h3>
          <p style={{ margin: 0 }}>
            You have{' '}
            {!privacyMode ? (
              <>
                {formatDecimals(lpData.lpTokenBalance, lpTokenDecimals)} LP tokens
                <br />
                <small style={{ color: 'gray' }}>
                  ~{formatCurrencyExtended(yourLpUSDValue)} in value
                </small>
              </>
            ) : (
              '*****'
            )}
          </p>

          {/* Quick Fill Percentage Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '10px' }}>
            {[25, 50, 75, 100].map((pct) => (
              <Button
                key={pct}
                label={`${pct}%`}
                onClick={() => handleQuickFillRemove(pct / 100)}
                className={
                  removePercent === pct
                    ? 'p-button-warning'
                    : 'p-button-outlined p-button-warning'
                }
                disabled={isLoading}
              />
            ))}
          </div>

          {/* Input Field for Exact Remove Amount */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: 'bold' }}>LP Tokens to Remove:</label>
            <InputText
              placeholder="0.0"
              value={removeAmount}
              onChange={(e) => onChangeRemoveAmount(e.target.value)}
              style={{ width: '100%', marginTop: '5px' }}
              disabled={isLoading}
            />
            {!privacyMode && (
              <small style={{ color: 'gray' }}>
                ~{formatCurrencyExtended(removeUsdValue)} USD
              </small>
            )}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '5px',
            marginBottom: '5px'
          }}>
            {/* Display Removing Details */}
            {removeAmount && !privacyMode && (
              <div style={{ marginBottom: '15px' }}>
                Removing {removeAmount} LP tokens
                <br />
                <small style={{ color: 'gray' }}>
                  ~{formatCurrencyExtended(removeUsdValue)} USD
                </small>
              </div>
            )}

            {/* Approve LP if needed */}
            {removeAmount && needsLpApproval() && (
              <Button
                label={`Approve`}
                onClick={handleApproveLpToken}
                disabled={isLoading}
                className="p-button-warning p-button-sm"
                style={{ marginLeft: '10px' }}
              />
            )}
          </div>
          <Button
            label={isLoading ? 'Processing...' : 'Remove Liquidity'}
            onClick={handleRemoveLiquidity}
            disabled={!canRemoveLiquidity}
            className="p-button-danger"
            style={{ width: '100%' }}
          />
        </>
      )}

    </Dialog>
  );
};

export default LpManageModal;
